import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { LoginCredentials } from '../types/user'
import logo from '../assets/logo-laranja.png'
import RequirePasswordResetModal from '../components/auth/RequirePasswordResetModal'
import { resolvePostLoginRedirect } from '../utils/postLoginRedirect'
import { getSystemVersionWithEnv } from '../utils/version'

export default function Login() {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false)
  const { login, isAuthenticated, user, error, clearError, changePassword } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.requirePasswordReset) {
        setShowPasswordResetModal(true)
      } else {
        navigate(resolvePostLoginRedirect(location.state), { replace: true })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate])

  const handlePasswordReset = async (currentPassword: string, newPassword: string) => {
    try {
      await changePassword(currentPassword, newPassword)
      setShowPasswordResetModal(false)
      navigate(resolvePostLoginRedirect(location.state), { replace: true })
    } catch (error) {
      console.error('Password reset failed:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    clearError()

    try {
      await login(credentials)
    } catch {
      // Error is handled by auth context
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-surface-50 text-brand-text-primary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-48 w-48 flex items-center justify-center">
            <img
              src={logo}
              alt="doBeco Padaria"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col items-center" style={{ marginTop: '-1.5rem', marginBottom: '2rem' }}>
            <h2 className="text-center text-3xl font-extrabold text-brand-text-primary">
              Controlo de Manutenção
            </h2>
            <p className="mt-2 text-center text-sm text-brand-text-muted">
              Introduza as suas credenciais para aceder ao sistema
            </p>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid="login-form">
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                data-testid="login-email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-brand-neutral-300 placeholder:text-brand-text-muted text-brand-text-primary rounded-t-md focus:outline-none focus:ring-brand-primary-500 focus:border-brand-primary-500 focus:z-10 sm:text-sm bg-white"
                placeholder="E-mail"
                value={credentials.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Palavra-Passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-brand-neutral-300 placeholder:text-brand-text-muted text-brand-text-primary rounded-b-md focus:outline-none focus:ring-brand-primary-500 focus:border-brand-primary-500 focus:z-10 sm:text-sm bg-white"
                placeholder="Palavra-Passe"
                value={credentials.password}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4" data-testid="login-error">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Erro de autenticação
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              {isLoading ? 'A entrar...' : 'Entrar'}
            </button>
          </div>
        </form>

        <div className="text-center text-xs text-brand-text-muted">
          doBeco Padaria - Sistema de Controlo de Manutenção
        </div>

        <div
          className="text-center text-xs text-brand-text-muted"
          data-testid="login-app-version"
        >
          {getSystemVersionWithEnv()}
        </div>

        {/* Link para esqueci minha senha */}
        <div className="mt-6 text-center">
          <a
            href="/forgot-password"
            className="font-medium text-brand-primary-500 hover:text-brand-primary-400 text-sm"
          >
            Esqueci a minha palavra-passe
          </a>
        </div>
      </div>

      {/* Modal de reset obrigatório */}
      <RequirePasswordResetModal
        isOpen={showPasswordResetModal}
        userName={user?.name || 'Utilizador'}
        onSubmit={handlePasswordReset}
        isLoading={isLoading}
      />
    </div>
  )
}